import React, { useEffect, useState } from 'react'
import { message } from 'antd'
import styled from 'styled-components/macro'
import {
  HARBOUR_NEWSLETTER_COPY_ACTION,
  HARBOUR_NEWSLETTER_COPY_TOAST,
  HARBOUR_NEWSLETTER_FOR_LABEL,
  HARBOUR_NEWSLETTER_LATER_ACTION,
  HARBOUR_NEWSLETTER_SENT_ACTION,
  HARBOUR_NEWSLETTER_SKIP_ACTION,
} from 'src/constants/harbour'
import {
  copyHarbourNewsletterBrief,
  getHarbourNewsletterReminderState,
  markHarbourNewsletterSent,
  remindHarbourNewsletterNextMonth,
  skipHarbourNewsletterTopic,
} from 'src/utils/harbourNewsletter'

/**
 * Compact CS newsletter reminder. Sits under How this board works, above
 * Pulse. Harbour does not send mail.
 */
const HarbourNewsletterReminder = ({ accountId } = {}) => {
  const [state, setState] = useState(() =>
    getHarbourNewsletterReminderState(accountId),
  )
  const [copyBusy, setCopyBusy] = useState(false)

  useEffect(() => {
    setState(getHarbourNewsletterReminderState(accountId))
  }, [accountId])

  const refresh = () => {
    setState(getHarbourNewsletterReminderState(accountId))
  }

  const handleCopy = async () => {
    if (copyBusy || !state.brief) {
      return
    }
    setCopyBusy(true)
    try {
      await copyHarbourNewsletterBrief(state.brief)
      message.success(HARBOUR_NEWSLETTER_COPY_TOAST)
    } catch (error) {
      message.error(error?.message || 'Could not copy brief')
    } finally {
      setCopyBusy(false)
    }
  }

  const handleSent = () => {
    markHarbourNewsletterSent(accountId)
    refresh()
  }

  const handleLater = () => {
    remindHarbourNewsletterNextMonth(accountId)
    refresh()
  }

  const handleSkip = () => {
    skipHarbourNewsletterTopic(accountId)
    refresh()
  }

  if (!accountId || !state.visible || !state.brief) {
    return null
  }

  const { brief } = state

  return (
    <Strip
      data-testid='harbour-newsletter'
      data-layout='compact'
      data-topic={brief.id}
    >
      <Head>
        <Eyebrow>This month: {brief.title}</Eyebrow>
        <Skip
          type='button'
          onClick={handleSkip}
          data-testid='harbour-newsletter-skip'
        >
          {HARBOUR_NEWSLETTER_SKIP_ACTION}
        </Skip>
      </Head>
      <Hook data-testid='harbour-newsletter-hook'>{brief.csHook}</Hook>
      <Audience data-testid='harbour-newsletter-audience'>
        {HARBOUR_NEWSLETTER_FOR_LABEL} {brief.audience}
      </Audience>
      <Benefits>
        {brief.benefits.map((line, index) => (
          <Benefit
            key={line}
            data-testid={`harbour-newsletter-benefit-${index}`}
          >
            {line}
          </Benefit>
        ))}
      </Benefits>
      <Actions>
        <Primary
          type='button'
          onClick={handleCopy}
          disabled={copyBusy}
          data-testid='harbour-newsletter-copy'
        >
          {HARBOUR_NEWSLETTER_COPY_ACTION}
        </Primary>
        <Secondary
          type='button'
          onClick={handleSent}
          data-testid='harbour-newsletter-sent'
        >
          {HARBOUR_NEWSLETTER_SENT_ACTION}
        </Secondary>
        <Secondary
          type='button'
          onClick={handleLater}
          data-testid='harbour-newsletter-later'
        >
          {HARBOUR_NEWSLETTER_LATER_ACTION}
        </Secondary>
      </Actions>
    </Strip>
  )
}

const Strip = styled.aside`
  padding: 8px 10px 8px 12px;
  border: 1px solid #e8c4a8;
  border-left: 4px solid #c45c26;
  border-radius: 8px;
  background: #fbf6f1;
`

const Head = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
`

const Eyebrow = styled.p`
  margin: 0;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: #c45c26;
`

const Skip = styled.button`
  border: 0;
  padding: 0;
  background: transparent;
  cursor: pointer;
  font-size: 12px;
  color: ${({ theme }) => theme.colours.oliveGrey || '#888'};
  text-decoration: underline;
`

const Hook = styled.p`
  margin: 4px 0 0;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.3;
  color: ${({ theme }) => theme.colours.broccoliGreen || '#1a1a1a'};
`

const Audience = styled.p`
  margin: 2px 0 0;
  font-size: 12px;
  line-height: 1.3;
  color: ${({ theme }) => theme.colours.stormGrey || '#666'};
`

const Benefits = styled.ul`
  margin: 6px 0 0;
  padding: 0;
  list-style: none;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 6px;

  @media (max-width: 52rem) {
    grid-template-columns: 1fr;
  }
`

const Benefit = styled.li`
  margin: 0;
  padding: 6px 8px;
  border-radius: 6px;
  background: #fff;
  border: 1px solid rgba(196, 92, 38, 0.12);
  font-size: 12px;
  line-height: 1.3;
  color: ${({ theme }) => theme.colours.broccoliGreen || '#1a1a1a'};
`

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
`

const Primary = styled.button`
  padding: 6px 12px;
  border: 1px solid #c45c26;
  border-radius: 6px;
  background: #c45c26;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.7;
    cursor: default;
  }
`

const Secondary = styled.button`
  padding: 6px 12px;
  border: 1px solid #e8c4a8;
  border-radius: 6px;
  background: #fff;
  color: ${({ theme }) => theme.colours.broccoliGreen || '#1a1a1a'};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
`

export default HarbourNewsletterReminder
